import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Upload, FileText, Download, Trash2, Search, X, Receipt, Calendar, Loader2, UserPlus, Eye, EyeOff, Sparkles, AlertTriangle, ChevronLeft, ChevronRight, FileSpreadsheet, FileDown, ReceiptText, Building2, FolderArchive } from "lucide-react";
import { useAdminLanguage } from "../contexts/AdminLanguageContext";
import DocumentPreviewModal from "./DocumentPreviewModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  "kvitto": "bg-green-500/10 text-green-400 border-green-500/30",
  "leverantörsfaktura": "bg-blue-500/10 text-blue-400 border-blue-500/30",
  "kundfaktura": "bg-[#F59073]/10 text-[#F59073] border-[#F59073]/30",
  "löpande kostnad": "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  "skatt": "bg-red-500/10 text-red-400 border-red-500/30",
  "lön": "bg-purple-500/10 text-purple-400 border-purple-500/30",
  "övrigt": "bg-[#1E3050]/40 text-[#857973] border-[#1E3050]",
};

const fmt = (n, ccy = "SEK") => `${(Number(n) || 0).toLocaleString("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;

export default function BookkeepingPage() {
  const { t, lang } = useAdminLanguage();
  const [tab, setTab] = useState("receipts");
  const [documents, setDocuments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [accountants, setAccountants] = useState([]);
  const [accForm, setAccForm] = useState({ email: "", name: "" });
  const [preview, setPreview] = useState(null); // { url, filename, contentType }

  // Upload + AI flow state
  const [pickedFile, setPickedFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [dropQueue, setDropQueue] = useState([]);
  const [confirmForm, setConfirmForm] = useState({
    vendor: "", description: "", category: "kvitto", date: "",
    total_amount: "", currency: "SEK", vat_amount: "", vat_percent: "",
    confidence: "medium", ai_extracted: false,
  });

  const fetchDocs = useCallback(async () => {
    const { data } = await axios.get(`${API}/bookkeeping/documents`, { withCredentials: true });
    setDocuments(data);
  }, []);

  const fetchInvoices = useCallback(async () => {
    const { data } = await axios.get(`${API}/bookkeeping/invoices`, { withCredentials: true });
    setInvoices(data);
  }, []);

  const fetchCategories = useCallback(async () => {
    const { data } = await axios.get(`${API}/bookkeeping/categories`, { withCredentials: true });
    setCategories(data);
  }, []);

  const fetchAccountants = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/accountants`, { withCredentials: true });
      setAccountants(data);
    } catch {}
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);
  useEffect(() => { fetchInvoices(); fetchCategories(); fetchAccountants(); }, [fetchInvoices, fetchCategories, fetchAccountants]);

  // ==== Upload + AI analyse flow ====
  const handleFilePick = async (file) => {
    if (!file) return;
    const isOtherTab = tab === "other";
    setPickedFile(file);
    setAnalyzeError("");
    setAnalyzing(true);
    // Pre-fill defaults from file metadata
    setConfirmForm(p => ({
      ...p, vendor: "", description: file.name?.replace(/\.[^.]+$/, "") || "",
      date: new Date().toISOString().slice(0, 10),
      total_amount: "", currency: "SEK", vat_amount: "", vat_percent: "",
      category: isOtherTab ? "övrigt" : "kvitto",
      confidence: "medium", ai_extracted: false,
    }));
    if (isOtherTab) {
      // No AI analysis for "Other" — user just adds metadata manually
      setAnalyzing(false);
      setShowConfirm(true);
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/bookkeeping/documents/analyze`, fd, { withCredentials: true });
      setConfirmForm({
        vendor: data.vendor || "",
        description: data.description || file.name?.replace(/\.[^.]+$/, "") || "",
        category: data.category || "övrigt",
        date: data.date || new Date().toISOString().slice(0, 10),
        total_amount: data.total_amount ?? "",
        currency: data.currency || "SEK",
        vat_amount: data.vat_amount ?? "",
        vat_percent: data.vat_percent ?? "",
        confidence: data.confidence || "medium",
        ai_extracted: true,
      });
    } catch (err) {
      setAnalyzeError(err.response?.data?.detail || t("bk_ai_failed"));
      setConfirmForm(p => ({ ...p, category: "övrigt", confidence: "low", ai_extracted: false }));
    }
    setAnalyzing(false);
    setShowConfirm(true);
  };

  const handleConfirmUpload = async (e) => {
    e.preventDefault();
    if (!pickedFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", pickedFile);
    fd.append("description", confirmForm.description || "");
    fd.append("category", confirmForm.category || "övrigt");
    fd.append("date", confirmForm.date || "");
    fd.append("vendor", confirmForm.vendor || "");
    fd.append("total_amount", String(confirmForm.total_amount ?? ""));
    fd.append("currency", confirmForm.currency || "");
    fd.append("vat_amount", String(confirmForm.vat_amount ?? ""));
    fd.append("vat_percent", String(confirmForm.vat_percent ?? ""));
    fd.append("ai_extracted", String(!!confirmForm.ai_extracted));
    try {
      await axios.post(`${API}/bookkeeping/documents/upload`, fd, { withCredentials: true });
      setShowConfirm(false);
      setPickedFile(null);
      fetchDocs();
    } catch (err) {
      alert(t("bk_save_failed") + ": " + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  const cancelUpload = () => {
    setShowConfirm(false);
    setPickedFile(null);
    setAnalyzing(false);
    setAnalyzeError("");
  };

  // Drag & drop: handle multiple files by queuing them and processing one at a time
  useEffect(() => {
    if (!showConfirm && !analyzing && !uploading && dropQueue.length > 0) {
      const [next, ...rest] = dropQueue;
      setDropQueue(rest);
      handleFilePick(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConfirm, analyzing, uploading, dropQueue]);

  const handleDragOver = (e) => {
    if (!["receipts", "supplier", "other"].includes(tab)) return;
    e.preventDefault();
    setDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!["receipts", "supplier", "other"].includes(tab)) return;
    const files = Array.from(e.dataTransfer?.files || []).filter(f =>
      f.type.startsWith("image/") || f.type === "application/pdf" || /\.(pdf|png|jpe?g|webp|heic|heif)$/i.test(f.name || "")
    );
    if (files.length === 0) return;
    setDropQueue(prev => [...prev, ...files]);
  };

  const handleDelete = async (docId) => {
    if (!window.confirm(t("bk_confirm_delete_doc"))) return;
    try {
      await axios.delete(`${API}/bookkeeping/documents/${docId}`, { withCredentials: true });
      fetchDocs();
    } catch {}
  };

  // Auth-aware download helper — needed because <a href> doesn't send the Bearer token
  const downloadAuthed = async (url, filename) => {
    try {
      const res = await axios.get(url, { withCredentials: true, responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      alert("Kunde inte ladda ner: " + (err.response?.statusText || err.message));
    }
  };

  const handleCreateAccountant = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/accountant`, accForm, { withCredentials: true });
      alert(t("bk_invite_sent", { email: accForm.email }));
      setAccForm({ email: "", name: "" });
      fetchAccountants();
    } catch (err) {
      alert(err.response?.data?.detail || "Could not send invite");
    }
  };

  const handleResendInvite = async (email) => {
    try {
      await axios.post(`${API}/admin/accountant/${email}/resend-invite`, {}, { withCredentials: true });
      alert(t("bk_invite_sent", { email }));
      fetchAccountants();
    } catch (err) {
      alert(err.response?.data?.detail || "Could not send invite");
    }
  };

  const handleDeleteAccountant = async (email) => {
    if (!window.confirm(t("bk_confirm_delete_acc", { email }))) return;
    try {
      await axios.delete(`${API}/admin/accountant/${email}`, { withCredentials: true });
      fetchAccountants();
    } catch {}
  };

  const filteredDocs = documents.filter(d => {
    if (search) {
      const q = search.toLowerCase();
      if (!(d.filename?.toLowerCase().includes(q) || d.description?.toLowerCase().includes(q) || d.vendor?.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  // Tab-specific document slices
  const TX_CATS = ["kvitto", "leverantörsfaktura", "kundfaktura"];
  const receipts = filteredDocs.filter(d => d.category === "kvitto");
  const supplierInvoices = filteredDocs.filter(d => d.category === "leverantörsfaktura");
  const otherDocs = filteredDocs.filter(d => !TX_CATS.includes(d.category));

  const filteredInvoices = invoices.filter(inv => {
    if (search) {
      const q = search.toLowerCase();
      return inv.invoice_number?.toLowerCase().includes(q) || inv.customer_name?.toLowerCase().includes(q);
    }
    return true;
  });

  // ==== Group by month for the selected year ====
  const MONTH_NAMES = t("months");
  const monthlyData = useMemo(() => {
    const months = MONTH_NAMES.map((name, idx) => ({
      name, idx,
      docs: [],
      invoices: [],
      totalExpenses: 0,
      totalIncome: 0,
      totalVat: 0,
      currencies: new Set(),
    }));
    filteredDocs.forEach(d => {
      const dt = (d.date || "").slice(0, 10);
      const y = parseInt(dt.slice(0, 4), 10);
      const m = parseInt(dt.slice(5, 7), 10) - 1;
      if (y === year && m >= 0 && m < 12) {
        months[m].docs.push(d);
        if (d.total_amount) months[m].totalExpenses += Number(d.total_amount) || 0;
        if (d.vat_amount) months[m].totalVat += Number(d.vat_amount) || 0;
        if (d.currency) months[m].currencies.add(d.currency);
      }
    });
    filteredInvoices.forEach(inv => {
      const dt = (inv.paid_date || inv.created_at || "").slice(0, 10);
      const y = parseInt(dt.slice(0, 4), 10);
      const m = parseInt(dt.slice(5, 7), 10) - 1;
      if (y === year && m >= 0 && m < 12) {
        months[m].invoices.push(inv);
        if (inv.status === "paid") {
          months[m].totalIncome += Number(inv.total_amount) || 0;
          if (inv.vat_amount) months[m].totalVat += Number(inv.vat_amount) || 0;
        }
        if (inv.currency) months[m].currencies.add(inv.currency);
      }
    });
    return months;
  }, [filteredDocs, filteredInvoices, year, MONTH_NAMES]);

  const yearTotals = useMemo(() => {
    let exp = 0, inc = 0, vat = 0;
    monthlyData.forEach(m => {
      exp += m.totalExpenses;
      inc += m.totalIncome;
      vat += m.totalVat;
    });
    return { exp, inc, vat };
  }, [monthlyData]);

  const tabs = [
    { key: "receipts", label: t("bk_tab_receipts"), icon: ReceiptText, count: receipts.length },
    { key: "supplier", label: t("bk_tab_supplier_invoices"), icon: Building2, count: supplierInvoices.length },
    { key: "customer", label: t("bk_tab_customer_invoices"), icon: Receipt, count: invoices.length },
    { key: "other", label: t("bk_tab_other"), icon: FolderArchive, count: otherDocs.length },
    { key: "monthly", label: t("bk_tab_monthly"), icon: Calendar, count: 0 },
    { key: "access", label: t("bk_tab_access"), icon: UserPlus, count: accountants.length },
  ];

  const exportMonthFile = async (kind) => {
    try {
      const isZip = kind === "zip";
      const endpoint = isZip ? "export-pdfs" : "export";
      const params = isZip ? { year, month: exportMonth } : { year, month: exportMonth, format: kind };
      const ext = isZip ? "zip" : (kind === "xlsx" ? "xlsx" : "csv");
      const res = await axios.get(`${API}/bookkeeping/${endpoint}`, {
        withCredentials: true, responseType: "blob", params,
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `bookkeeping-${year}-${String(exportMonth).padStart(2, "0")}${isZip ? "-pdfs" : ""}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      alert("Export failed: " + (err.response?.statusText || err.message));
    }
  };

  return (
    <div data-testid="bookkeeping-page" onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className="relative">
      {dragOver && ["receipts", "supplier", "other"].includes(tab) && (
        <div className="fixed inset-0 z-40 bg-[#F59073]/10 backdrop-blur-sm border-4 border-dashed border-[#F59073] flex items-center justify-center pointer-events-none" data-testid="drop-overlay">
          <div className="bg-[#0A1628]/90 border border-[#F59073]/50 rounded-sm px-8 py-6 flex flex-col items-center gap-3">
            <Upload className="w-12 h-12 text-[#F59073]" />
            <span className="font-outfit text-xl text-white">{t("bk_drop_overlay")}</span>
          </div>
        </div>
      )}
      {dropQueue.length > 0 && (
        <div className="fixed bottom-4 right-4 z-30 bg-[#0F1F3A] border border-[#F59073]/30 rounded-sm px-4 py-2 flex items-center gap-2" data-testid="drop-queue-indicator">
          <Loader2 className="w-3.5 h-3.5 text-[#F59073] animate-spin" />
          <span className="text-xs text-white">{t("bk_queue", { n: dropQueue.length + (pickedFile ? 1 : 0) })}</span>
        </div>
      )}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">{t("bk_title")}</h1>
          <p className="text-sm text-[#857973]">{t("bk_subtitle_count", { docs: documents.length, invoices: invoices.length })}</p>
          {["receipts", "supplier", "other"].includes(tab) && (
            <p className="text-[11px] text-[#F59073]/70 mt-1 flex items-center gap-1.5"><Upload className="w-3 h-3" />{t("bk_drop_hint")}</p>
          )}
        </div>
        {(tab === "receipts" || tab === "supplier" || tab === "other") && (
          <label data-testid="upload-document-btn" className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all cursor-pointer ${analyzing ? "bg-[#1E3050] text-[#857973]" : "bg-[#F59073] text-black hover:shadow-[0_0_15px_rgba(245,144,115,0.3)]"}`}>
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {analyzing ? t("bk_analyzing") : t("bk_upload_btn")}
            <input type="file" accept=".pdf,image/*" data-testid="upload-file-input" disabled={analyzing}
              className="hidden"
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleFilePick(f); }} />
          </label>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#1E3050] mb-6 overflow-x-auto">
        {tabs.map(({ key, label, icon: Icon, count }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap transition-colors border-b-2 ${tab === key ? "border-[#F59073] text-[#F59073]" : "border-transparent text-[#857973] hover:text-white"}`}>
            <Icon className="w-4 h-4" /> {label}
            {count > 0 && <span className="text-xs bg-[#1E3050]/40 px-1.5 py-0.5 rounded-sm">{count}</span>}
          </button>
        ))}
      </div>

      {/* Search */}
      {(tab === "receipts" || tab === "supplier" || tab === "customer" || tab === "other") && (
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857973]" />
            <input data-testid="bookkeeping-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t("bk_search_placeholder")}
              className="w-full bg-[#0F1F3A] border border-[#1E3050] text-white pl-10 pr-4 py-2.5 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
          </div>
        </div>
      )}

      {/* Receipts Tab */}
      {tab === "receipts" && (
        <DocumentList docs={receipts.filter(d => true)} t={t} downloadAuthed={downloadAuthed} handleDelete={handleDelete} fmt={fmt} CATEGORY_COLORS={CATEGORY_COLORS} emptyLabel={t("bk_tab_receipts").toLowerCase()} setPreview={setPreview} />
      )}

      {/* Supplier Invoices Tab */}
      {tab === "supplier" && (
        <DocumentList docs={supplierInvoices} t={t} downloadAuthed={downloadAuthed} handleDelete={handleDelete} fmt={fmt} CATEGORY_COLORS={CATEGORY_COLORS} emptyLabel={t("bk_tab_supplier_invoices").toLowerCase()} setPreview={setPreview} />
      )}

      {/* Other / Company Documents Tab */}
      {tab === "other" && (
        <DocumentList docs={otherDocs} t={t} downloadAuthed={downloadAuthed} handleDelete={handleDelete} fmt={fmt} CATEGORY_COLORS={CATEGORY_COLORS} emptyLabel={t("bk_tab_other").toLowerCase()} setPreview={setPreview} />
      )}

      {/* Customer Invoices Tab */}
      {tab === "customer" && (
        <div className="space-y-2">
          {filteredInvoices.map(inv => (
            <div key={inv.id} data-testid={`inv-${inv.id}`} className="flex items-center justify-between bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <Receipt className="w-5 h-5 text-[#F59073] flex-shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm text-white">{inv.invoice_number || inv.id.slice(0, 8).toUpperCase()}</span>
                    <span className="text-sm text-[#857973]">{inv.customer_name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-sm font-mono border ${inv.status === "paid" ? "bg-green-500/10 text-green-400 border-green-500/30" : inv.status === "sent" ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/30" : "bg-[#1E3050]/40 text-[#857973] border-[#1E3050]"}`}>
                      {inv.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#857973]">
                    <span>{inv.created_at?.slice(0, 10)}</span>
                    {inv.paid_date && <span>{t("bk_status_paid")}: {inv.paid_date}</span>}
                    {inv.type === "recurring" && <span className="text-purple-400">recurring</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                <span className="text-sm text-white font-mono">{fmt(inv.total_amount, inv.currency || "SEK")}</span>
                <button onClick={() => setPreview({ url: `${API}/bookkeeping/invoices/${inv.id}/pdf`, filename: `invoice-${inv.invoice_number || inv.id.slice(0,8).toUpperCase()}.pdf` })} data-testid={`inv-preview-${inv.id}`}
                  className="p-2 text-[#857973] hover:text-[#F59073] transition-colors" title="Preview">
                  <Eye className="w-4 h-4" />
                </button>
                <button onClick={() => downloadAuthed(`${API}/bookkeeping/invoices/${inv.id}/pdf`, `invoice-${inv.invoice_number || inv.id.slice(0,8).toUpperCase()}.pdf`)} data-testid={`inv-pdf-${inv.id}`}
                  className="p-2 text-[#857973] hover:text-[#F59073] transition-colors" title="Download PDF">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {filteredInvoices.length === 0 && (
            <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-12 text-center">
              <Receipt className="w-8 h-8 text-[#1E3050] mx-auto mb-3" />
              <p className="text-[#857973]">{t("bk_no_items", { label: t("bk_tab_customer_invoices").toLowerCase() })}</p>
            </div>
          )}
        </div>
      )}

      {/* Monthly Tab */}
      {tab === "monthly" && (
        <div className="space-y-4">
          {/* Year selector + summary */}
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setYear(y => y - 1)} data-testid="year-prev"
                className="p-1.5 text-[#857973] hover:text-white border border-[#1E3050] rounded-sm"><ChevronLeft className="w-4 h-4" /></button>
              <span className="font-outfit text-xl text-white" data-testid="year-display">{year}</span>
              <button onClick={() => setYear(y => y + 1)} data-testid="year-next"
                className="p-1.5 text-[#857973] hover:text-white border border-[#1E3050] rounded-sm"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 text-xs md:flex md:flex-wrap md:items-center">
              <div><div className="text-[#857973] font-mono uppercase tracking-wider">{t("bk_summary_income")}</div><div className="text-green-400 font-mono text-base mt-0.5 truncate">{fmt(yearTotals.inc)}</div></div>
              <div><div className="text-[#857973] font-mono uppercase tracking-wider">{t("bk_summary_expenses")}</div><div className="text-red-400 font-mono text-base mt-0.5 truncate">{fmt(yearTotals.exp)}</div></div>
              <div><div className="text-[#857973] font-mono uppercase tracking-wider">{t("bk_summary_vat")}</div><div className="text-[#F59073] font-mono text-base mt-0.5 truncate">{fmt(yearTotals.vat)}</div></div>
              <div><div className="text-[#857973] font-mono uppercase tracking-wider">{t("bk_summary_total")}</div><div className={`font-mono text-base mt-0.5 truncate ${yearTotals.inc - yearTotals.exp >= 0 ? "text-green-400" : "text-red-400"}`}>{fmt(yearTotals.inc - yearTotals.exp)}</div></div>
            </div>
          </div>

          {/* Export bar */}
          <div className="flex items-center gap-3 bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-3 flex-wrap" data-testid="export-bar">
            <span className="text-xs font-mono text-[#857973] tracking-wider uppercase">{t("bk_export_btn")}:</span>
            <select value={exportMonth} onChange={e => setExportMonth(Number(e.target.value))} data-testid="export-month-select"
              className="bg-[#0A1628] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-xs focus:border-[#F59073] outline-none">
              {MONTH_NAMES.map((name, i) => <option key={i} value={i + 1}>{name}</option>)}
            </select>
            <span className="text-xs text-[#857973] font-mono">{year}</span>
            <button onClick={() => exportMonthFile("zip")} data-testid="export-pdfs-btn"
              className="inline-flex items-center gap-1.5 bg-[#F59073] text-black px-3 py-1.5 rounded-sm text-xs font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all">
              <FileText className="w-3.5 h-3.5" /> {t("bk_export_pdfs")}
            </button>
            <button onClick={() => exportMonthFile("csv")} data-testid="export-csv-btn"
              className="inline-flex items-center gap-1.5 bg-[#1E3050] text-white px-3 py-1.5 rounded-sm text-xs font-medium hover:bg-[#1E3050]/70 transition-all border border-[#1E3050]">
              <FileDown className="w-3.5 h-3.5" /> {t("bk_export_csv")}
            </button>
            <button onClick={() => exportMonthFile("xlsx")} data-testid="export-xlsx-btn"
              className="inline-flex items-center gap-1.5 bg-[#1E3050] text-white px-3 py-1.5 rounded-sm text-xs font-medium hover:bg-[#1E3050]/70 transition-all border border-[#1E3050]">
              <FileSpreadsheet className="w-3.5 h-3.5" /> {t("bk_export_xlsx")}
            </button>
          </div>

          {/* Months */}
          {monthlyData.map(m => {
            const hasData = m.docs.length > 0 || m.invoices.length > 0;
            return (
              <details key={m.idx} className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm" open={hasData}>
                <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#1E3050]/20 list-none">
                  <div className="flex items-center gap-3">
                    <span className="font-outfit text-base text-white">{m.name}</span>
                    {hasData ? (
                      <span className="text-xs text-[#857973] font-mono">{t("bk_docs_count", { n: m.docs.length })} &middot; {t("bk_invoices_count", { n: m.invoices.length })}</span>
                    ) : (
                      <span className="text-xs text-[#857973] font-mono">{t("bk_empty_month")}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-5 text-xs font-mono">
                    {m.totalIncome > 0 && <span className="text-green-400">+{fmt(m.totalIncome)}</span>}
                    {m.totalExpenses > 0 && <span className="text-red-400">-{fmt(m.totalExpenses)}</span>}
                    {m.totalVat > 0 && <span className="text-[#F59073]">{t("bk_summary_vat")} {fmt(m.totalVat)}</span>}
                  </div>
                </summary>
                {hasData && (
                  <div className="border-t border-[#1E3050] divide-y divide-[#1E3050]/60">
                    {m.docs.map(doc => (
                      <div key={`d-${doc.id}`} className="flex items-center justify-between p-3 px-4 text-sm hover:bg-[#1E3050]/10">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <FileText className="w-4 h-4 text-red-400/70 flex-shrink-0" />
                          <span className="text-white truncate">{doc.vendor || doc.filename}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-mono border whitespace-nowrap ${CATEGORY_COLORS[doc.category] || CATEGORY_COLORS["övrigt"]}`}>{t(`cat_${doc.category}`)}</span>
                          <span className="text-xs text-[#857973] whitespace-nowrap">{doc.date}</span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {doc.total_amount != null && <span className="text-red-400 font-mono">-{fmt(doc.total_amount, doc.currency || "SEK")}</span>}
                          <button onClick={() => setPreview({ url: `${API}/bookkeeping/documents/${doc.id}/download`, filename: doc.filename || `document-${doc.id.slice(0,8)}` })} className="text-[#857973] hover:text-[#F59073]" title="Preview"><Eye className="w-3.5 h-3.5" /></button>
                          <button onClick={() => downloadAuthed(`${API}/bookkeeping/documents/${doc.id}/download`, doc.filename || `dokument-${doc.id.slice(0,8)}`)} className="text-[#857973] hover:text-[#F59073]"><Download className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                    {m.invoices.map(inv => (
                      <div key={`i-${inv.id}`} className="flex items-center justify-between p-3 px-4 text-sm hover:bg-[#1E3050]/10">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Receipt className="w-4 h-4 text-green-400/70 flex-shrink-0" />
                          <span className="text-white truncate">{inv.invoice_number || inv.id.slice(0, 8).toUpperCase()}</span>
                          <span className="text-xs text-[#857973] truncate">{inv.customer_name}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-mono border whitespace-nowrap ${inv.status === "paid" ? "bg-green-500/10 text-green-400 border-green-500/30" : "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"}`}>{inv.status}</span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className={`font-mono ${inv.status === "paid" ? "text-green-400" : "text-[#857973]"}`}>+{fmt(inv.total_amount, inv.currency || "SEK")}</span>
                          <button onClick={() => setPreview({ url: `${API}/bookkeeping/invoices/${inv.id}/pdf`, filename: `invoice-${inv.invoice_number || inv.id.slice(0,8).toUpperCase()}.pdf` })} className="text-[#857973] hover:text-[#F59073]" title="Preview"><Eye className="w-3.5 h-3.5" /></button>
                          <button onClick={() => downloadAuthed(`${API}/bookkeeping/invoices/${inv.id}/pdf`, `invoice-${inv.invoice_number || inv.id.slice(0,8).toUpperCase()}.pdf`)} className="text-[#857973] hover:text-[#F59073]"><Download className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </details>
            );
          })}
        </div>
      )}

      {/* Access Tab */}
      {tab === "access" && (
        <div className="space-y-6">
          <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-6">
            <h3 className="font-outfit font-medium text-white mb-1">{t("bk_create_accountant")}</h3>
            <p className="text-xs text-[#857973] mb-4">{t("bk_acc_subtitle")}</p>
            <form onSubmit={handleCreateAccountant} className="flex flex-col sm:flex-row gap-3">
              <input value={accForm.name} onChange={e => setAccForm(p => ({ ...p, name: e.target.value }))}
                placeholder={t("bk_acc_name")} data-testid="acc-name"
                className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none flex-1" />
              <input value={accForm.email} onChange={e => setAccForm(p => ({ ...p, email: e.target.value }))}
                placeholder={t("bk_acc_email")} type="email" required data-testid="acc-email"
                className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none flex-1" />
              <button type="submit" data-testid="create-accountant-btn"
                className="bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all whitespace-nowrap">
                {t("bk_acc_create_btn")}
              </button>
            </form>
          </div>

          {accountants.length > 0 && (
            <div>
              <h3 className="font-mono text-[10px] tracking-[0.15em] text-[#857973] mb-3">{t("bk_active_accountants")}</h3>
              <div className="space-y-2">
                {accountants.map(acc => (
                  <div key={acc.email} className="flex items-center justify-between bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white truncate">{acc.name || t("bk_accountant")}</span>
                          {acc.pending && <span className="text-[9px] font-mono bg-yellow-500/15 text-yellow-400 border border-yellow-500/30 px-1.5 py-0.5 rounded-sm tracking-wider">{t("bk_acc_pending")}</span>}
                        </div>
                        <span className="text-xs text-[#857973]">{acc.email}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {acc.pending && (
                        <button onClick={() => handleResendInvite(acc.email)} data-testid={`resend-acc-${acc.email}`}
                          className="text-xs bg-[#1E3050] text-[#F59073] px-3 py-1.5 rounded-sm hover:bg-[#1E3050]/70 transition-colors">
                          {t("bk_resend_invite")}
                        </button>
                      )}
                      <button onClick={() => handleDeleteAccountant(acc.email)} data-testid={`delete-acc-${acc.email}`}
                        className="p-2 text-[#857973] hover:text-red-400 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-6">
            <h3 className="font-mono text-[10px] tracking-[0.15em] text-[#857973] mb-3">{t("bk_login_instructions")}</h3>
            <p className="text-sm text-[#857973] font-manrope leading-relaxed">{t("bk_login_text")}</p>
          </div>
        </div>
      )}

      {/* AI Confirm Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={cancelUpload} />
          <div className="relative bg-[#0F1F3A] border border-[#1E3050] rounded-sm w-full max-w-2xl p-6 z-10 max-h-[90vh] overflow-y-auto" data-testid="confirm-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#F59073]" />
                <h3 className="font-outfit font-medium text-white text-lg">{t("bk_confirm_title")}</h3>
              </div>
              <button onClick={cancelUpload} className="text-[#857973] hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <p className="text-xs text-[#857973] mb-3">{t("bk_confirm_subtitle")}</p>

            <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-3 mb-4 flex items-center gap-3">
              <FileText className="w-4 h-4 text-[#F59073] flex-shrink-0" />
              <span className="text-sm text-white truncate flex-1">{pickedFile?.name}</span>
              <span className="text-xs text-[#857973] font-mono whitespace-nowrap">{t("bk_size_kb", { n: pickedFile ? (pickedFile.size / 1024).toFixed(1) : 0 })}</span>
            </div>

            {analyzeError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-3 mb-4 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-red-300">{analyzeError}</span>
              </div>
            )}

            <form onSubmit={handleConfirmUpload} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-mono text-[#857973] block mb-1">{t("bk_field_vendor").toUpperCase()}</label>
                  <input type="text" value={confirmForm.vendor} onChange={e => setConfirmForm(p => ({ ...p, vendor: e.target.value }))}
                    data-testid="confirm-vendor"
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-mono text-[#857973] block mb-1">{t("bk_field_date").toUpperCase()}</label>
                  <input type="date" value={confirmForm.date} onChange={e => setConfirmForm(p => ({ ...p, date: e.target.value }))}
                    data-testid="confirm-date"
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-mono text-[#857973] block mb-1">{t("bk_field_description").toUpperCase()}</label>
                <input type="text" value={confirmForm.description} onChange={e => setConfirmForm(p => ({ ...p, description: e.target.value }))}
                  data-testid="confirm-description"
                  className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] font-mono text-[#857973] block mb-1">{t("bk_field_total").toUpperCase()}</label>
                  <input type="number" step="0.01" value={confirmForm.total_amount} onChange={e => setConfirmForm(p => ({ ...p, total_amount: e.target.value }))}
                    data-testid="confirm-total"
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-mono text-[#857973] block mb-1">{t("bk_field_currency").toUpperCase()}</label>
                  <select value={confirmForm.currency} onChange={e => setConfirmForm(p => ({ ...p, currency: e.target.value }))}
                    data-testid="confirm-currency"
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                    {["SEK", "EUR", "USD", "GBP", "NOK", "DKK"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-mono text-[#857973] block mb-1">{t("bk_field_category").toUpperCase()}</label>
                  <select value={confirmForm.category} onChange={e => setConfirmForm(p => ({ ...p, category: e.target.value }))}
                    data-testid="confirm-category"
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                    {categories.map(c => <option key={c} value={c}>{t(`cat_${c}`)}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-mono text-[#857973] block mb-1">{t("bk_field_vat").toUpperCase()}</label>
                  <input type="number" step="0.01" value={confirmForm.vat_amount} onChange={e => setConfirmForm(p => ({ ...p, vat_amount: e.target.value }))}
                    data-testid="confirm-vat-amount"
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-mono text-[#857973] block mb-1">{t("bk_field_vat_percent").toUpperCase()}</label>
                  <input type="number" step="0.1" value={confirmForm.vat_percent} onChange={e => setConfirmForm(p => ({ ...p, vat_percent: e.target.value }))}
                    data-testid="confirm-vat-percent"
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={cancelUpload}
                  className="flex-1 bg-[#1E3050] text-white py-2.5 rounded-sm text-sm font-medium hover:bg-[#1E3050]/70 transition-all">
                  {t("bk_cancel")}
                </button>
                <button type="submit" disabled={uploading} data-testid="confirm-save-btn"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#F59073] text-black py-2.5 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all disabled:opacity-50">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? t("bk_uploading") : t("bk_save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {preview && (
        <DocumentPreviewModal url={preview.url} filename={preview.filename} contentType={preview.contentType} onClose={() => setPreview(null)} />
      )}

    </div>
  );
}

function DocumentList({ docs, t, downloadAuthed, handleDelete, fmt, CATEGORY_COLORS, emptyLabel, setPreview }) {
  return (
    <div className="space-y-2">
      {docs.map(doc => (
        <div key={doc.id} data-testid={`doc-${doc.id}`} className="flex items-center justify-between bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4 hover:border-[#1E3050] transition-all cursor-pointer" onClick={() => setPreview && setPreview({ url: `${API}/bookkeeping/documents/${doc.id}/download`, filename: doc.filename || `document-${doc.id.slice(0,8)}` })}>
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <FileText className="w-5 h-5 text-[#F59073] flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <span className="text-sm text-white truncate">{doc.vendor || doc.filename}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-sm font-mono border ${CATEGORY_COLORS[doc.category] || CATEGORY_COLORS["övrigt"]}`}>
                  {t(`cat_${doc.category}`)}
                </span>
                {doc.ai_extracted && <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-mono bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/30 flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" />{t("bk_label_ai")}</span>}
              </div>
              <div className="flex items-center gap-3 text-xs text-[#857973] flex-wrap">
                <span>{doc.date}</span>
                {doc.description && <span className="truncate max-w-[300px]">{doc.description}</span>}
                <span className="font-mono">{t("bk_size_kb", { n: (doc.size / 1024).toFixed(1) })}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0" onClick={e => e.stopPropagation()}>
            {doc.total_amount != null && (
              <span className="text-sm text-white font-mono whitespace-nowrap">{fmt(doc.total_amount, doc.currency || "SEK")}</span>
            )}
            <button onClick={(e) => { e.stopPropagation(); setPreview({ url: `${API}/bookkeeping/documents/${doc.id}/download`, filename: doc.filename || `document-${doc.id.slice(0,8)}` }); }} data-testid={`preview-${doc.id}`}
              className="p-2 text-[#857973] hover:text-[#F59073] transition-colors" title="Preview">
              <Eye className="w-4 h-4" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); downloadAuthed(`${API}/bookkeeping/documents/${doc.id}/download`, doc.filename || `document-${doc.id.slice(0,8)}`); }} data-testid={`download-${doc.id}`}
              className="p-2 text-[#857973] hover:text-[#F59073] transition-colors">
              <Download className="w-4 h-4" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }} data-testid={`delete-${doc.id}`}
              className="p-2 text-[#857973] hover:text-red-400 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
      {docs.length === 0 && (
        <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-12 text-center">
          <FileText className="w-8 h-8 text-[#1E3050] mx-auto mb-3" />
          <p className="text-[#857973]">{t("bk_no_items", { label: emptyLabel })}</p>
        </div>
      )}
    </div>
  );
}
